import React, { useRef, useState } from "react";
import { project, prefersReducedMotion, rubberband, runSpring } from "./motion";
import "./swipe.css";

// A tappable row you can swipe left to delete. The row follows the finger, carries a flick, and resists when
// pulled right. Mouse and keyboard users get a Delete button instead. Mark controls inside the row (a checkbox,
// for example) with data-row-nodrag so they keep working and never start a swipe.
export default function SwipeRow({ active = false, onTap, onDelete, deleteLabel, children }) {
  const wrapRef = useRef(null);
  const rowRef = useRef(null);
  const live = useRef({ x: 0, cancel: null, gesture: null, moved: false });
  const [leaving, setLeaving] = useState(false);

  const setX = (v) => {
    live.current.x = v;
    if (rowRef.current) rowRef.current.style.transform = `translateX(${v}px)`;
  };

  const animateTo = (to, velocity, response, done) => {
    if (live.current.cancel) live.current.cancel();
    live.current.cancel = null;
    if (prefersReducedMotion()) {
      setX(to);
      if (done) done();
      return;
    }
    live.current.cancel = runSpring({
      from: live.current.x,
      to,
      velocity,
      zeta: 1,
      response,
      onUpdate: (x) => setX(x),
      onDone: () => {
        live.current.cancel = null;
        if (done) done();
      },
    });
  };

  const collapseThenDelete = () => {
    const wrap = wrapRef.current;
    if (!wrap || prefersReducedMotion()) {
      onDelete();
      return;
    }
    wrap.style.height = `${wrap.offsetHeight}px`;
    setLeaving(true);
    requestAnimationFrame(() => {
      wrap.style.height = "0px";
    });
    setTimeout(() => onDelete(), 210);
  };

  const onPointerDown = (e) => {
    if (e.target.closest("[data-row-nodrag]")) return;
    if (live.current.cancel) {
      live.current.cancel();
      live.current.cancel = null;
    }
    live.current.moved = false;
    live.current.gesture = { sx: e.clientX, sy: e.clientY, base: live.current.x, engaged: false, samples: [[performance.now(), e.clientX]] };
  };

  const onPointerMove = (e) => {
    const g = live.current.gesture;
    if (!g) return;
    const dx = e.clientX - g.sx;
    const dy = e.clientY - g.sy;
    if (!g.engaged) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return; // small movement is still a tap
      if (Math.abs(dy) > Math.abs(dx)) {
        live.current.gesture = null; // vertical: let the list scroll
        return;
      }
      g.engaged = true;
      live.current.moved = true;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {
        /* synthetic pointers cannot be captured */
      }
    }
    const raw = g.base + dx;
    const width = rowRef.current ? rowRef.current.offsetWidth : 300;
    setX(raw > 0 ? rubberband(raw, width) : raw);
    const now = performance.now();
    g.samples.push([now, e.clientX]);
    while (g.samples.length > 2 && now - g.samples[0][0] > 100) g.samples.shift();
  };

  const endGesture = () => {
    const g = live.current.gesture;
    live.current.gesture = null;
    if (!g || !g.engaged) return;
    const first = g.samples[0];
    const last = g.samples[g.samples.length - 1];
    const dt = (last[0] - first[0]) / 1000;
    const velocity = dt > 0 ? (last[1] - first[1]) / dt : 0;
    const width = rowRef.current ? rowRef.current.offsetWidth : 300;
    const projected = live.current.x + project(velocity) * 0.4;
    if (projected < -width * 0.5) animateTo(-width - 20, velocity, 0.25, collapseThenDelete);
    else animateTo(0, velocity, 0.35);
  };

  const open = () => {
    if (live.current.moved) {
      live.current.moved = false; // the click that ends a swipe is not a tap
      return;
    }
    onTap();
  };

  return (
    <div ref={wrapRef} className={`sw-wrap${leaving ? " is-leaving" : ""}`}>
      <div className="sw-bg" aria-hidden="true">Delete</div>
      <div
        ref={rowRef}
        className={`sw-row${active ? " is-active" : ""}`}
        role="button"
        tabIndex={0}
        aria-current={active ? "true" : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
        onClick={open}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return; // keys inside a control inside the row are the control's own
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onTap();
          } else if (e.key === "Delete" || e.key === "Backspace") {
            e.preventDefault();
            onDelete();
          }
        }}
      >
        {children}
      </div>
      <button type="button" className="sw-delete" data-row-nodrag aria-label={deleteLabel} onClick={() => onDelete()}>
        Delete
      </button>
    </div>
  );
}
